/*
 * MrBeast landing page (templates/page.mrbeast.json)
 * Custom elements used by the mrbeast-* sections.
 */

/* ---------- Featured products: tabs + carousel arrows ---------- */
if (!window.customElements.get('mrb-featured-products')) {
  class MrbFeaturedProducts extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this.panels = Array.from(this.querySelectorAll('[data-mrb-panel]'));
      this._renderTabs();
      this.tabs = Array.from(this.querySelectorAll('[data-mrb-tab]'));
      this.nav = this.querySelector('[data-mrb-nav]');
      this.prevButton = this.querySelector('[data-mrb-prev]');
      this.nextButton = this.querySelector('[data-mrb-next]');
      this.update = this.update.bind(this);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab.dataset.mrbTab), { signal });
      });

      this.prevButton?.addEventListener('click', () => this.step(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.step(1), { signal });

      this.tracks.forEach((track) => track.addEventListener('scroll', this.update, { passive: true, signal }));
      window.addEventListener('resize', this.update, { signal });
      window.addEventListener('load', this.update, { signal });

      // Track sizes are only final once the card images have laid out.
      if (window.ResizeObserver) {
        this._resizeObserver = new ResizeObserver(this.update);
        this.tracks.forEach((track) => this._resizeObserver.observe(track));
      }

      // In the theme editor, open the tab that holds the selected tab or card block.
      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('[data-mrb-panel]');

        if (panel) {
          this.select(panel.dataset.mrbPanel, { resetScroll: false });
        }
      }, { signal });

      this.select(this.panels[0]?.dataset.mrbPanel, { resetScroll: false });
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this._abortController.abort();
      this._resizeObserver?.disconnect();
    }

    // The section can't read its Tab blocks' settings in Liquid, so each panel carries its translated name.
    _renderTabs() {
      const tabList = this.querySelector('[data-mrb-tabs]');

      if (!tabList || this.panels.length < 2) {
        return;
      }

      tabList.replaceChildren(...this.panels.map((panel) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mrb-fp__tab';
        button.dataset.mrbTab = panel.dataset.mrbPanel;
        button.setAttribute('aria-controls', panel.id);
        button.textContent = panel.dataset.mrbLabel;
        return button;
      }));

      tabList.hidden = false;
    }

    get tracks() {
      return Array.from(this.querySelectorAll('[data-mrb-track]'));
    }

    get activeTrack() {
      return this.querySelector('[data-mrb-panel].is-active [data-mrb-track]');
    }

    select(panelId, { resetScroll = true } = {}) {
      if (!panelId) {
        return;
      }

      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.mrbTab === panelId;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-pressed', String(isActive));
      });

      this.panels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.mrbPanel === panelId));

      if (resetScroll) {
        this.activeTrack?.scrollTo({ left: 0, behavior: 'auto' });
      }

      this.update();
    }

    step(direction) {
      const track = this.activeTrack;

      if (!track) {
        return;
      }

      const card = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth;

      // In right-to-left languages scrollLeft runs from 0 to negative values, so "next" moves left.
      const sign = getComputedStyle(track).direction === 'rtl' ? -1 : 1;

      track.scrollBy({ left: distance * direction * sign, behavior: 'smooth' });
    }

    update() {
      const track = this.activeTrack;

      if (!track || !this.nav) {
        return;
      }

      const overflows = track.scrollWidth > track.clientWidth + 1;
      this.nav.classList.toggle('is-not-needed', !overflows);

      if (!overflows) {
        return;
      }

      const position = Math.abs(track.scrollLeft);
      const maxPosition = track.scrollWidth - track.clientWidth - 1;

      this.prevButton.disabled = position <= 1;
      this.nextButton.disabled = position >= maxPosition;
    }
  }

  window.customElements.define('mrb-featured-products', MrbFeaturedProducts);
}

/* ---------- Background video: desktop/mobile source, loads near the screen ---------- */
if (!window.customElements.get('mrb-bg-video')) {
  class MrbBgVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');

      // Visitors who ask their device to reduce motion keep the still image, and the video is never downloaded.
      if (!this.video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      this._mobileQuery = window.matchMedia('(max-width: 768px)');
      this._onBreakpointChange = this._load.bind(this);
      this.video.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });

      this._observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this._observer.disconnect();
          this._mobileQuery.addEventListener('change', this._onBreakpointChange);
          this._load();
        }
      }, { rootMargin: '200px 0px' });

      this._observer.observe(this);
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this._mobileQuery?.removeEventListener('change', this._onBreakpointChange);
    }

    // Swapping the source restarts the video, so this only runs on load and when the breakpoint changes.
    _load() {
      const source = (this._mobileQuery.matches && this.dataset.srcMobile) || this.dataset.srcDesktop;

      if (!source || this.video.getAttribute('src') === source) {
        return;
      }

      this.video.setAttribute('src', source);
      this.video.load();
      this.video.play()?.catch(() => {});
    }
  }

  window.customElements.define('mrb-bg-video', MrbBgVideo);
}
